/**
 * The Strip Card Results tab (ISSUE-1293), mounted by
 * `features/poolScreen/poolTypePanes.tsx`. After v1's strip_result_tab.dart:
 * a filter row (members, strip, and the game when the pool has more than
 * one), then a table of winners: Member, Strip, Spot (the digit) and Notes,
 * newest first as the server sorts them. An admin gets an Edit button per
 * row. The filter choices come from the server's own `filters`.
 */
import { useStripsResultsGet } from "@sportsmet/contest-sdk/private";
import {
  Button,
  cn,
  FOCUS_RING,
  InlineBanner,
  Select,
  Skeleton,
} from "@sportsmet/ui";
import { keepPreviousData, QueryClientContext } from "@tanstack/react-query";
import { type ReactNode, useContext, useState } from "react";

import type { typePoolTypePaneProps } from "../poolScreen/poolTypePaneProps";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import {
  STRIP_RESULTS_ALL_GAMES,
  STRIP_RESULTS_ALL_MEMBERS,
  STRIP_RESULTS_ALL_STRIPS,
  STRIP_RESULTS_COLUMN_EDIT,
  STRIP_RESULTS_COLUMN_MEMBER,
  STRIP_RESULTS_COLUMN_NOTES,
  STRIP_RESULTS_COLUMN_SPOT,
  STRIP_RESULTS_COLUMN_STRIP,
  STRIP_RESULTS_EDIT_LABEL,
  STRIP_RESULTS_EMPTY,
  STRIP_RESULTS_EMPTY_FILTERED,
  STRIP_RESULTS_GAME_LABEL,
  STRIP_RESULTS_HEADING,
  STRIP_RESULTS_LOAD_FAILED,
  STRIP_RESULTS_MEMBERS_LABEL,
  STRIP_RESULTS_STRIP_LABEL,
  STRIP_TRY_AGAIN_LABEL,
  stripResultsEditAriaLabel,
  stripResultsMembersSummary,
} from "./stripCardCopy";
import { StripMembersFilterSheet } from "./StripMembersFilterSheet";
import { stripDateLabel } from "./stripTime";
import {
  StripWinnerEditSheet,
  type typeStripResultsWinner,
} from "./StripWinnerEditSheet";

const ALL = "all";

export const StripCardResultsTab = (
  props: typePoolTypePaneProps,
): ReactNode => {
  // The pane registry's own test mounts every pane bare, with no providers.
  const hasQueryClient = useContext(QueryClientContext) !== undefined;
  return (
    <div
      className="flex flex-col gap-4"
      data-testid="strip-card-results-tab"
      data-pool-id={props.poolId}
    >
      {hasQueryClient ? <StripResultsBody poolId={props.poolId} /> : null}
    </div>
  );
};

const StripResultsBody = ({ poolId }: { poolId: string }): ReactNode => {
  const timeZone = resolveViewerTimeZone();
  const [userIds, setUserIds] = useState<string[]>([]);
  const [stripFilter, setStripFilter] = useState<string>(ALL);
  const [gameFilter, setGameFilter] = useState<string>(ALL);
  const [isMembersOpen, setIsMembersOpen] = useState(false);
  const [editing, setEditing] = useState<typeStripResultsWinner | null>(null);

  const read = useStripsResultsGet(
    {
      poolId,
      eventId: gameFilter === ALL ? null : gameFilter,
      userIds,
      stripNumber: stripFilter === ALL ? null : Number(stripFilter),
    },
    { placeholderData: keepPreviousData },
  );
  // While a new filter loads, keep showing the filters from the last answer.
  const data = read.data?.data ?? null;
  const isFiltered =
    userIds.length > 0 || stripFilter !== ALL || gameFilter !== ALL;

  if (read.isPending && data === null) {
    return (
      <div className="flex flex-col gap-3" data-testid="strip-results-loading">
        <Skeleton shape="card" />
        <Skeleton shape="card" />
      </div>
    );
  }
  if (data === null) {
    return (
      <InlineBanner
        variant="warning"
        message={STRIP_RESULTS_LOAD_FAILED}
        action={{
          label: STRIP_TRY_AGAIN_LABEL,
          onAction: () => {
            void read.refetch();
          },
        }}
      />
    );
  }

  const { filters, winners } = data;
  const canManage = data.viewer.canManage;
  const showGameColumn = filters.games.length > 1 && gameFilter === ALL;

  return (
    <>
      <h2 className="text-body-strong text-ink">{STRIP_RESULTS_HEADING}</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="flex flex-col gap-2">
          <span className="text-caption text-ink">
            {STRIP_RESULTS_MEMBERS_LABEL}
          </span>
          <button
            type="button"
            data-testid="strip-results-members"
            onClick={() => {
              setIsMembersOpen(true);
            }}
            className={cn(
              "flex h-control-height items-center rounded-sm border border-line bg-surface px-3 text-left text-input text-ink",
              FOCUS_RING,
            )}
          >
            {userIds.length === 0
              ? STRIP_RESULTS_ALL_MEMBERS
              : stripResultsMembersSummary(userIds.length)}
          </button>
        </div>
        <Select
          label={STRIP_RESULTS_STRIP_LABEL}
          value={stripFilter}
          onValueChange={setStripFilter}
          items={[
            { value: ALL, label: STRIP_RESULTS_ALL_STRIPS },
            ...filters.strips.map((strip) => ({
              value: String(strip.stripNumber),
              label: strip.label,
            })),
          ]}
        />
        {filters.games.length > 1 ? (
          <Select
            label={STRIP_RESULTS_GAME_LABEL}
            value={gameFilter}
            onValueChange={setGameFilter}
            items={[
              { value: ALL, label: STRIP_RESULTS_ALL_GAMES },
              ...filters.games.map((game) => ({
                value: game.eventId,
                label: game.label,
              })),
            ]}
          />
        ) : null}
      </div>

      {winners.length === 0 ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="strip-results-empty"
        >
          {isFiltered ? STRIP_RESULTS_EMPTY_FILTERED : STRIP_RESULTS_EMPTY}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line bg-surface">
          <table
            className="w-full border-collapse text-left"
            data-testid="strip-results-table"
          >
            <thead>
              <tr className="border-b border-line">
                <th
                  scope="col"
                  className="px-3 py-2 text-small text-ink-secondary"
                >
                  {STRIP_RESULTS_COLUMN_MEMBER}
                </th>
                <th
                  scope="col"
                  className="px-2 py-2 text-small text-ink-secondary"
                >
                  {STRIP_RESULTS_COLUMN_STRIP}
                </th>
                <th
                  scope="col"
                  className="px-2 py-2 text-small text-ink-secondary"
                >
                  {STRIP_RESULTS_COLUMN_SPOT}
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-small text-ink-secondary"
                >
                  {STRIP_RESULTS_COLUMN_NOTES}
                </th>
                {canManage ? (
                  <th scope="col" className="px-2 py-2">
                    <span className="sr-only">{STRIP_RESULTS_COLUMN_EDIT}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {winners.map((winner) => (
                <tr
                  key={winner.stripWinnerId}
                  data-testid={`strip-result-${winner.stripWinnerId}`}
                  className="border-b border-line last:border-b-0 align-top"
                >
                  <td className="px-3 py-3">
                    <span className="block text-body text-ink">
                      {winner.displayName}
                    </span>
                    {showGameColumn ? (
                      <span className="block text-small text-ink-secondary">
                        {winner.gameLabel}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-2 py-3 text-body text-ink">
                    {winner.stripLabel}
                  </td>
                  <td className="px-2 py-3 text-body-strong text-ink tabular-nums">
                    {winner.digit}
                  </td>
                  <td className="px-3 py-3">
                    <span className="block text-body text-ink whitespace-pre-line">
                      {winner.note}
                    </span>
                    <span className="block text-small text-ink-secondary">
                      {stripDateLabel({ iso: winner.wonAt, timeZone })}
                    </span>
                  </td>
                  {canManage ? (
                    <td className="px-2 py-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={stripResultsEditAriaLabel(
                          winner.displayName,
                        )}
                        data-testid={`strip-result-edit-${winner.stripWinnerId}`}
                        onClick={() => {
                          setEditing(winner);
                        }}
                      >
                        {STRIP_RESULTS_EDIT_LABEL}
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <StripMembersFilterSheet
        open={isMembersOpen}
        members={filters.members}
        selected={userIds}
        onClose={() => {
          setIsMembersOpen(false);
        }}
        onApply={setUserIds}
      />
      {editing !== null ? (
        <StripWinnerEditSheet
          poolId={poolId}
          winner={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </>
  );
};
